import { Fragment, useCallback, useEffect, useMemo, useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'
import { Dialog, Transition } from '@headlessui/react'
import { MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import { api, useVerticalModule, type RecordSearchResult } from '@lipaflow/shared'
import { useNavigation, type NavChild, type NavItem } from '../../lib/useNavigation'

interface Command {
  id: string
  label: string
  group: string
  path: string
  icon: React.ComponentType<{ className?: string }>
  keywords?: string[]
}

function flattenNavToCommands(items: NavItem[]): Command[] {
  const commands: Command[] = []
  const add = (page: NavChild, group: string) =>
    commands.push({
      id: page.href.replace(/^\//, '') || 'home',
      label: page.name,
      group,
      path: page.href,
      icon: page.icon,
      keywords: page.keywords,
    })
  for (const item of items) {
    if (item.children) {
      for (const child of item.children) {
        // An entry with tabs is several pages: search finds each of them.
        if (child.tabs) child.tabs.forEach((tab) => add(tab, child.name))
        else add(child, item.name)
      }
    } else if (item.tabs) {
      item.tabs.forEach((tab) => add(tab, item.name))
    } else if (item.href !== undefined && item.icon) {
      add({ name: item.name, href: item.href, icon: item.icon, keywords: item.keywords }, 'Navigation')
    }
  }
  return commands
}

function fuzzyMatch(text: string, query: string): boolean {
  const lowerText = text.toLowerCase()
  const lowerQuery = query.toLowerCase()
  let qi = 0
  for (let ti = 0; ti < lowerText.length && qi < lowerQuery.length; ti++) {
    if (lowerText[ti] === lowerQuery[qi]) qi++
  }
  return qi === lowerQuery.length
}

interface CommandPaletteProps {
  open: boolean
  setOpen: (update: boolean | ((prev: boolean) => boolean)) => void
}

/**
 * Open state belongs to the layout, so the top bar's Search button can open
 * it directly. (It used to fake a Cmd+K keydown on `window`, which never
 * reaches this listener on `document`: the button did nothing.)
 */
export default function CommandPalette({ open, setOpen }: CommandPaletteProps) {
  const [query, setQuery] = useState('')
  const navigate = useNavigate()
  const { navigation } = useNavigation()

  const commands = useMemo(() => flattenNavToCommands(navigation), [navigation])
  // Things, not just pages: the vertical finds its records by name, phone or reference.
  const recordSearch = useVerticalModule()?.recordSearch
  const [term, setTerm] = useState('')
  useEffect(() => {
    const t = setTimeout(() => setTerm(query.trim()), 200)
    return () => clearTimeout(t)
  }, [query])
  const records = useQuery({
    queryKey: ['record-search', recordSearch?.path, term],
    queryFn: async () => (await api.get<RecordSearchResult[]>(recordSearch!.path, { params: { q: term } })).data,
    enabled: open && !!recordSearch && term.length >= 2,
    staleTime: 30_000,
  })
  const found = useMemo(() => (term.length >= 2 ? records.data ?? [] : []), [term, records.data])

  // Keyboard shortcut: Cmd+K / Ctrl+K
  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault()
        setOpen((prev) => !prev)
      }
    }
    document.addEventListener('keydown', onKeyDown)
    return () => document.removeEventListener('keydown', onKeyDown)
  }, [setOpen])

  const filtered = useMemo(() => {
    if (!query) return commands
    return commands.filter(
      (cmd) =>
        fuzzyMatch(cmd.label, query) ||
        cmd.keywords?.some((kw) => fuzzyMatch(kw, query)) ||
        fuzzyMatch(cmd.group, query)
    )
  }, [query, commands])

  const groups = useMemo(() => {
    const map = new Map<string, Command[]>()
    for (const cmd of filtered) {
      const list = map.get(cmd.group) || []
      list.push(cmd)
      map.set(cmd.group, list)
    }
    return map
  }, [filtered])

  // One list for the keyboard: records first, then pages, in the order shown.
  const choices = useMemo(() => [...found, ...Array.from(groups.values()).flat()], [found, groups])
  const [active, setActive] = useState(0)
  useEffect(() => setActive(0), [query, found.length])

  const handleSelect = useCallback(
    (cmd: { path: string }) => {
      setOpen(false)
      setQuery('')
      navigate(cmd.path)
    },
    [navigate, setOpen]
  )

  return (
    <Transition show={open} as={Fragment}>
      <Dialog onClose={() => { setOpen(false); setQuery('') }} className="relative z-50">
        <Transition.Child
          as={Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>
        <div className="fixed inset-0 flex items-start justify-center pt-[20vh] p-4">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-lg rounded-card bg-surface shadow-2xl ring-1 ring-hairline overflow-hidden">
              <div className="flex items-center gap-3 border-b border-hairline px-4">
                <MagnifyingGlassIcon className="h-5 w-5 text-ink-faint" />
                <input
                  type="text"
                  value={query}
                  onChange={(e) => setQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
                      e.preventDefault()
                      const step = e.key === 'ArrowDown' ? 1 : -1
                      setActive((i) => (choices.length ? (i + step + choices.length) % choices.length : 0))
                    } else if (e.key === 'Enter' && choices[active]) {
                      e.preventDefault()
                      handleSelect(choices[active])
                    }
                  }}
                  role="combobox"
                  aria-expanded
                  aria-controls="command-palette-list"
                  aria-activedescendant={choices[active] ? `command-${active}` : undefined}
                  placeholder={recordSearch ? 'Search units, people, owners or pages' : 'Search pages'}
                  aria-label="Search"
                  className="flex-1 py-3 bg-transparent text-sm text-ink placeholder-ink-faint outline-none border-none focus:ring-0"
                  autoFocus
                />
                <kbd className="hidden sm:inline-block px-1.5 py-0.5 text-[10px] font-medium text-ink-faint bg-surface-alt rounded">
                  ESC
                </kbd>
              </div>
              <div id="command-palette-list" role="listbox" aria-label="Results" className="max-h-72 overflow-y-auto p-2">
                {found.length > 0 && (
                  <div className="mb-2">
                    <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-ink-faint">Records</p>
                    {found.map((r, i) => (
                      <button key={`${r.kind}-${r.id}`} id={`command-${i}`} role="option" aria-selected={active === i} tabIndex={-1}
                        onClick={() => handleSelect(r)} onMouseEnter={() => setActive(i)}
                        className={`flex w-full flex-col items-start rounded-button px-3 py-2 text-left text-sm text-ink ${active === i ? 'bg-accent-soft' : ''}`}>
                        <span>{r.title}</span>
                        {r.subtitle && <span className="text-caption text-ink-muted">{r.subtitle}</span>}
                      </button>
                    ))}
                  </div>
                )}
                {filtered.length === 0 && found.length === 0 ? (
                  <p className="py-6 text-center text-sm text-ink-muted">{records.isFetching ? 'Searching…' : 'No results found'}</p>
                ) : (
                  Array.from(groups.entries()).map(([group, cmds]) => (
                    <div key={group} className="mb-2">
                      <p className="px-3 py-1 text-[11px] font-semibold uppercase tracking-wider text-ink-faint">{group}</p>
                      {cmds.map((cmd) => {
                        const i = choices.indexOf(cmd)
                        return (
                        <button
                          key={cmd.id}
                          id={`command-${i}`}
                          role="option"
                          aria-selected={active === i}
                          tabIndex={-1}
                          onClick={() => handleSelect(cmd)}
                          onMouseEnter={() => setActive(i)}
                          className={`flex w-full items-center gap-3 rounded-button px-3 py-2 text-sm text-ink ${active === i ? 'bg-accent-soft' : ''}`}
                        >
                          <cmd.icon className="h-4 w-4 text-ink-faint" />
                          {cmd.label}
                        </button>
                        )
                      })}
                    </div>
                  ))
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  )
}
